import React, { useState, useEffect, useRef } from 'react';
import { Send, Settings } from 'lucide-react';
import { Assistant } from '@/types';
import { gtmService } from '@/services/retargeting/gtm.service';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';
import GPTLogoWhite from '@/assets/GPTforme Logo White.svg';
import DriveSourceLogoWhite from '@/assets/DriveSource Logo White.svg';
import HomeSourceLogoWhite from '@/assets/HomeSource Logo White.svg';
import { getCookieConsent } from '@/utils/cookieConsent';

interface ChatInputProps {
    assistant: Assistant;
    onSendMessage: (message: string) => void;
    disabled?: boolean;
    showBanner: () => void;
    firstMessageTracked?: boolean;
    inline?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
                                                        assistant,
                                                        onSendMessage,
                                                        disabled,
                                                        showBanner,
                                                        firstMessageTracked = false,
                                                        inline = false,
                                                    }) => {
    const [input, setInput] = useState('');
    const [cookieConsent, setCookieConsent] = useState<string | null>(null);
    const [hasTrackedFirstMessage, setHasTrackedFirstMessage] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // Handle iOS keyboard - use focus/blur instead of visualViewport
    const handleFocus = () => {
        setIsKeyboardOpen(true);
        if (isIOS) {
            // On iOS, scroll the input into view after keyboard opens
            setTimeout(() => {
                textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 300);
        }
    };

    const handleBlur = () => {
        setIsKeyboardOpen(false);
        if (isIOS) {
            // Force iOS to recalculate layout after keyboard closes
            setTimeout(() => {
                window.scrollTo(0, window.scrollY);
                // Force a repaint
                if (containerRef.current) {
                    containerRef.current.style.transform = 'translateZ(0)';
                    requestAnimationFrame(() => {
                        if (containerRef.current) {
                            containerRef.current.style.transform = '';
                        }
                    });
                }
            }, 100);
        }
    };

    useEffect(() => {
        const checkConsent = () => {
            const consent = getCookieConsent();
            setCookieConsent(consent);
        };

        checkConsent();

        const handleStorageChange = () => {
            checkConsent();
        };

        window.addEventListener('storage', handleStorageChange);
        const interval = setInterval(checkConsent, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Auto-resize textarea based on content
    useEffect(() => {
        if (textareaRef.current) {
            // Reset height to auto to get the correct scrollHeight
            textareaRef.current.style.height = 'auto';
            // Set height based on scrollHeight, with a max height
            const maxHeight = 200; // Maximum height in pixels (about 8-10 lines)
            const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
            textareaRef.current.style.height = `${newHeight}px`;
        }
    }, [input]);

    const isChatDisabled = cookieConsent === 'declined' || disabled;
    const hasNotConsented = cookieConsent !== 'accepted';
    const isInputDisabled = cookieConsent === 'declined';

    const handleSubmit = () => {
        if (input.trim() && !isChatDisabled) {
            const message = input.trim();

            // Track first message sent in this page visit
            if (!firstMessageTracked && !hasTrackedFirstMessage) {
                // Track with Google Ads using GTM service
                gtmService.trackFirstMessage(assistant.name);

                // Track with Facebook Pixel service
                fbPixelService.trackChatStarted();

                setHasTrackedFirstMessage(true);
            }

            onSendMessage(message);
            setInput('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !isChatDisabled && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const getPlaceholderText = () => {
        if (cookieConsent === 'declined') {
            return 'Chat disabled - cookies declined';
        }
        if (hasNotConsented) {
            return 'Accept cookies to chat...';
        }
        return import.meta.env.VITE_CHAT_PLACEHOLDER || `Ask ${assistant.name} anything...`;
    };

    if (inline) {
        return (
            <div ref={containerRef} className="w-full mt-3 sm:mt-4">
                <div className="max-w-3xl mx-auto">
                    {cookieConsent === 'declined' && (
                        <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-red-900/50 border border-red-700 rounded-lg">
                            <div className="flex items-center justify-between gap-3">
                                <p className="text-red-300 text-xs sm:text-sm flex-1">
                                    <strong>Chat Disabled:</strong> You've declined cookies. Please accept cookies to use the chat feature.
                                </p>
                                <button
                                    onClick={showBanner}
                                    className="bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all duration-300 ease-in-out px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                                >
                                    <Settings size={14} className="sm:w-4 sm:h-4" />
                                    <span>Cookie Settings</span>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col gap-0.5 sm:gap-1">
                        <div className="relative">
                            <textarea
                                ref={textareaRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                placeholder={getPlaceholderText()}
                                rows={1}
                                className={`w-full px-4 sm:px-5 py-3.5 sm:py-4 pr-14 text-sm sm:text-base border rounded-2xl focus:outline-none transition-all text-white placeholder-gray-400 bg-[#383838] border-gray-400 resize-none overflow-hidden ${
                                    isInputDisabled
                                        ? 'opacity-60'
                                        : 'focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400'
                                }`}
                                disabled={isInputDisabled}
                                style={{ minHeight: '54px' }}
                            />
                            <button
                                onClick={handleSubmit}
                                disabled={isChatDisabled || !input.trim()}
                                className={`absolute right-2.5 top-[11px] w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    isChatDisabled || !input.trim()
                                        ? 'bg-gray-600 cursor-not-allowed'
                                        : 'bg-cyan-400 hover:bg-cyan-500'
                                }`}
                            >
                                <Send className="w-3.5 h-3.5 text-black" />
                            </button>
                        </div>
                        <div className="px-1 mt-1">
                            <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed text-center">
                                {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME || 'GPTforme'} provides general information only and may make mistakes. Please consult a qualified expert before making decisions.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="fixed left-0 right-0 bg-[#1a1a1a] z-40"
            style={{
                bottom: 0,
                paddingBottom: 'env(safe-area-inset-bottom)',
                // Prevent iOS from hiding this behind keyboard
                WebkitTransform: 'translate3d(0,0,0)',
                transform: 'translate3d(0,0,0)'
            }}
        >
            <div className="max-w-3xl mx-auto px-1.5 py-1.5 sm:px-3 sm:py-2 md:p-4">
                {cookieConsent === 'declined' && (
                    <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-red-900/50 border border-red-700 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-red-300 text-xs sm:text-sm flex-1">
                                <strong>Chat Disabled:</strong> You've declined cookies. Please accept cookies to use the chat feature.
                            </p>
                            <button
                                onClick={showBanner}
                                className="bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all duration-300 ease-in-out px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
                            >
                                <Settings size={14} className="sm:w-4 sm:h-4" />
                                <span>Cookie Settings</span>
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-0.5 sm:gap-1 md:gap-2">
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={getPlaceholderText()}
                            rows={1}
                            className={`w-full px-4 sm:px-5 py-3.5 sm:py-4 pr-14 text-sm sm:text-base border rounded-2xl focus:outline-none transition-all text-white placeholder-gray-400 bg-[#383838] border-gray-400 resize-none overflow-hidden ${
                                isInputDisabled
                                    ? 'opacity-60'
                                    : 'focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400'
                            }`}
                            disabled={isInputDisabled}
                            style={{ minHeight: '54px' }}
                        />
                        <button
                            onClick={handleSubmit}
                            disabled={isChatDisabled || !input.trim()}
                            className={`absolute right-2.5 top-[11px] w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                isChatDisabled || !input.trim()
                                    ? 'bg-gray-600 cursor-not-allowed'
                                    : 'bg-cyan-400 hover:bg-cyan-500'
                            }`}
                        >
                            <Send className="w-3.5 h-3.5 text-black" />
                        </button>
                    </div>
                    <div className="px-0.5 sm:px-1">
                        {import.meta.env.VITE_TAGLINE && (
                            <p className="text-[9px] sm:text-[10px] md:text-xs text-white leading-tight mb-0.5">
                                {import.meta.env.VITE_TAGLINE}
                            </p>
                        )}
                        <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-400 leading-tight">
                            {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME || 'GPTforme'} provides general information only and may make mistakes. It does not offer professional, medical, legal, or financial advice. Please consult a qualified expert before making decisions.
                        </p>
                    </div>
                </div>
            </div>
            {/* Full-width row: Cross-link (left) and Powered by + Privacy/Terms (right) at input height */}
            <div className="hidden sm:flex absolute left-0 right-0 top-1/2 -translate-y-1/2 items-center justify-between px-2 sm:px-4 md:px-6 lg:px-8 pointer-events-none">
                {/* Cross-link to other environment */}
                {(() => {
                    const dn = import.meta.env.VITE_ASSISTANT_DISPLAY_NAME;
                    const crossLink = dn === 'DriveSource'
                        ? { label: 'HomeSource', url: 'https://gethomesource.com', logo: HomeSourceLogoWhite }
                        : dn === 'HomeSource'
                            ? { label: 'DriveSource', url: 'https://getdrivesource.com', logo: DriveSourceLogoWhite }
                            : null;
                    return crossLink ? (
                        <a
                            href={crossLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-300 transition-colors pointer-events-auto"
                        >
                            <span className="text-xs sm:text-sm whitespace-nowrap">Visit</span>
                            <img src={crossLink.logo} alt={crossLink.label} className="h-3 sm:h-4 w-auto opacity-60 hover:opacity-100 transition-opacity" />
                        </a>
                    ) : <div />;
                })()}
                {/* Powered by + Privacy/Terms stacked on right */}
                <div className="flex flex-col items-end gap-0.5 pointer-events-auto">
                    <div className="flex items-center gap-1 text-gray-400">
                        <span className="text-xs sm:text-sm whitespace-nowrap">Powered by</span>
                        <img src={GPTLogoWhite} alt="GPTforme" className="h-4 sm:h-5 w-auto opacity-60" />
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <a href="/GPTforme_privacy_policy.pdf" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-gray-400 hover:text-cyan-400 transition-colors whitespace-nowrap">
                            Privacy Policy
                        </a>
                        <span className="text-gray-600 text-xs">|</span>
                        <a href="/GPTforme_terms_of_use.pdf" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-gray-400 hover:text-cyan-400 transition-colors whitespace-nowrap">
                            Terms of Service
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};